/**
 * Lightbox s navigací mezi více fotkami (šipky vlevo/vpravo + klávesy).
 *
 * Použití:
 *   openLightbox({ photos: [{ url, filename, bucket, path, photoId }], startIndex, onDelete })
 *   nebo zpětně kompatibilní:
 *   openLightbox({ url, filename, bucket, path, photoId, onDelete })  // 1 fotka
 */
import { supabase } from './supabase.js';
import { getMediaType, MUSIC_ICON_SVG } from './media.js';
import { rotateStoredImage } from './image-rotate.js';

const state = {
    photos: [],
    index: 0,
    onDelete: null
};

function ensureContainer() {
    let lb = document.getElementById('lightbox');
    if (lb) return lb;
    lb = document.createElement('div');
    lb.id = 'lightbox';
    lb.className = 'lightbox';
    lb.innerHTML = `
        <button class="lb-nav lb-prev" type="button" title="Předchozí (← )">‹</button>
        <div id="lb-media"></div>
        <button class="lb-nav lb-next" type="button" title="Další (→)">›</button>
        <div class="lb-controls">
            <span class="lb-counter" id="lb-counter"></span>
            <button class="lb-btn" id="lb-rotate">↻ Otočit</button>
            <button class="lb-btn" id="lb-download">Stáhnout</button>
            <button class="lb-btn lb-danger" id="lb-delete">Smazat</button>
            <button class="lb-btn" id="lb-close">Zavřít</button>
        </div>
    `;
    document.body.appendChild(lb);

    lb.addEventListener('click', (e) => { if (e.target === lb) closeLightbox(); });
    lb.querySelector('#lb-close').addEventListener('click', closeLightbox);
    lb.querySelector('.lb-prev').addEventListener('click', prev);
    lb.querySelector('.lb-next').addEventListener('click', next);

    document.addEventListener('keydown', (e) => {
        if (!lb.classList.contains('open')) return;
        // Při textovém inputu / video controls neprovádět navigaci
        const tag = e.target?.tagName;
        if (e.key === 'Escape') { closeLightbox(); return; }
        if (tag === 'VIDEO' || tag === 'AUDIO') return;
        if (e.key === 'ArrowLeft')  prev();
        if (e.key === 'ArrowRight') next();
    });

    return lb;
}

function closeLightbox() {
    const lb = document.getElementById('lightbox');
    if (lb) lb.classList.remove('open');
    document.body.style.overflow = '';
}

function showCurrent() {
    const lb = document.getElementById('lightbox');
    if (!lb) return;
    const p = state.photos[state.index];
    if (!p) return;

    const mediaBox = lb.querySelector('#lb-media');
    const type = getMediaType(p.path || p.filename || p.url);
    // Zastav předchozí média
    mediaBox.querySelectorAll('video, audio').forEach(el => { try { el.pause(); } catch {} });

    if (type === 'video') {
        mediaBox.innerHTML = `<video controls autoplay playsinline src="${p.url}"></video>`;
    } else if (type === 'audio') {
        mediaBox.innerHTML = `
            <div class="audio-wrap">
                <div class="icon">${MUSIC_ICON_SVG}</div>
                <audio controls autoplay src="${p.url}"></audio>
                <div class="fn">${p.filename || ''}</div>
            </div>
        `;
    } else {
        mediaBox.innerHTML = `<img src="${p.url}" alt="">`;
    }

    const counter = lb.querySelector('#lb-counter');
    counter.textContent = state.photos.length > 1
        ? `${state.index + 1} / ${state.photos.length}`
        : '';

    lb.querySelector('.lb-prev').style.visibility = state.photos.length > 1 ? '' : 'hidden';
    lb.querySelector('.lb-next').style.visibility = state.photos.length > 1 ? '' : 'hidden';

    // Download
    lb.querySelector('#lb-download').onclick = () => downloadFromUrl(p.url, p.filename);

    // Otočit (jen u fotek, kde známe úložiště)
    const rotateBtn = lb.querySelector('#lb-rotate');
    if (type === 'image' && p.bucket && p.path) {
        rotateBtn.style.display = '';
        rotateBtn.onclick = async () => {
            rotateBtn.disabled = true;
            const orig = rotateBtn.textContent;
            rotateBtn.textContent = 'Otáčím…';
            try {
                await rotateStoredImage(p.bucket, p.path, p.url);
                const img = mediaBox.querySelector('img');
                if (img) {
                    img.src = p.url + (p.url.includes('?') ? '&' : '?') + 'r=' + Date.now();
                }
                state.onDelete?.();   // obnoví náhledy v galerii na pozadí
            } catch (e) {
                alert('Otočení se nezdařilo: ' + (e.message || e));
            } finally {
                rotateBtn.disabled = false;
                rotateBtn.textContent = orig;
            }
        };
    } else {
        rotateBtn.style.display = 'none';
    }

    // Delete (jen pokud máme path + photoId + bucket)
    const deleteBtn = lb.querySelector('#lb-delete');
    if (p.bucket && p.path && p.photoId) {
        deleteBtn.style.display = '';
        deleteBtn.onclick = async () => {
            if (!confirm('Opravdu smazat tuto fotku?')) return;
            await supabase.storage.from(p.bucket).remove([p.path]);
            await supabase.from('photos').delete().eq('id', p.photoId);
            // Odstraň z lokálního seznamu
            state.photos.splice(state.index, 1);
            if (state.photos.length === 0) {
                closeLightbox();
                state.onDelete?.();
                return;
            }
            if (state.index >= state.photos.length) state.index = state.photos.length - 1;
            showCurrent();
            state.onDelete?.();
        };
    } else {
        deleteBtn.style.display = 'none';
    }
}

function prev() {
    if (state.photos.length < 2) return;
    state.index = (state.index - 1 + state.photos.length) % state.photos.length;
    showCurrent();
}
function next() {
    if (state.photos.length < 2) return;
    state.index = (state.index + 1) % state.photos.length;
    showCurrent();
}

async function downloadFromUrl(url, filename) {
    try {
        const r = await fetch(url);
        const blob = await r.blob();
        const dl = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = dl;
        a.download = filename || 'foto.jpg';
        document.body.appendChild(a);
        a.click();
        a.remove();
        setTimeout(() => URL.revokeObjectURL(dl), 1000);
    } catch (e) {
        alert('Chyba při stahování: ' + e.message);
    }
}

export function openLightbox(opts = {}) {
    const lb = ensureContainer();
    state.onDelete = opts.onDelete || null;

    // Array varianta
    if (Array.isArray(opts.photos)) {
        state.photos = opts.photos;
        state.index  = Math.max(0, Math.min(opts.startIndex || 0, state.photos.length - 1));
    } else {
        // Zpětně kompatibilní 1-photo varianta
        state.photos = [{
            url: opts.url, filename: opts.filename,
            bucket: opts.bucket, path: opts.path, photoId: opts.photoId
        }];
        state.index = 0;
    }

    if (state.photos.length === 0) return;

    showCurrent();
    lb.classList.add('open');
    document.body.style.overflow = 'hidden';
}
